import { create } from "zustand";
import { supabase } from "./supabase";
import { Track } from "@/types/track";
import { ilikeContainsPattern, isRankedSearch } from "@/lib/search-ranking";

export type ViewMode = "grid" | "list";

export interface AlbumView {
  id: string;
  title: string;
  artist: string;
  coverUrl: string | null;
  releaseDate: string;
  tracks: Track[];
  genre: string;
  description?: string;
  isPublic: boolean;
  type: "album" | "ep" | "single";
  label?: string;
  totalTracks: number;
  totalLength?: number;
  createdAt: string;
  updatedAt: string;
  isVirtual: true;
  albumName: string;
  albumCategory?: string;
}

const PAGE_SIZE = 50;

// One select for the sorted query and the ranked function, so a card is built from the same shape.
const ALBUM_SELECT = "*, artists:artist_id ( name )";

function getAlbumType(trackCount: number): "single" | "ep" | "album" {
  if (trackCount === 1) return "single";
  if (trackCount <= 6) return "ep";
  return "album";
}

interface AlbumFilters<Q> {
  eq(column: "label_tag", value: string): Q;
  in(column: "label_tag", values: readonly string[]): Q;
  ilike(column: "title", pattern: string): Q;
}

/**
 * Which albums match: label scoping + the title search. Shared by the sorted list query and the
 * ranked path's count, so the count always describes the same rows as the grid.
 */
function filterAlbums<Q extends AlbumFilters<Q>>(
  query: Q,
  labelTag: string | null | undefined,
  filterLabelTags: string[],
  term: string
): Q {
  // Label scoping: seller's label takes precedence, then buyer's filter
  if (labelTag) {
    query = query.eq("label_tag", labelTag);
  } else if (filterLabelTags.length > 0) {
    query = query.in("label_tag", filterLabelTags);
  }

  // Server-side search by album title
  if (term) {
    query = query.ilike("title", ilikeContainsPattern(term));
  }
  return query;
}

/**
 * The query behind the albums on screen. A reset load commits it in the same `set` as its rows, so
 * whatever describes those rows reads this, not `searchQuery`: the keystrokes run ahead of the
 * debounced reload, and the rows answer the search that was applied.
 */
interface AppliedAlbumsQuery {
  /**
   * The reset load that committed these rows. A load-more continues this query only while this is
   * still the store's `_generation`: any newer reset (search, sort, label filter or label change, or
   * `reset()`) retires it, so a page can never be appended into a different list.
   */
  generation: number;
  /** The trimmed search term; "" when the rows are not a search. */
  search: string;
  /** "Best match": ordered by search_published_albums instead of the sort. */
  ranked: boolean;
  sortBy: AlbumsState["sortBy"];
  sortOrder: AlbumsState["sortOrder"];
}

interface AlbumsState {
  /** Wipe album data + counts + selection + filters (logout/account switch). Keeps view/sort prefs. */
  reset: () => void;
  albums: AlbumView[];
  /** Exact count of the albums the applied query matches. Counted on a reset only; a load-more keeps it. */
  totalCount: number;
  hasMore: boolean;
  pageIndex: number;
  isFetchingMore: boolean;
  lastLabelTag: string | null;
  isSelectionMode: boolean;
  selectedAlbumIds: Set<string>;
  viewMode: ViewMode;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  /** What the albums on screen were loaded with. `null` until the first load commits, and after `reset()`. */
  appliedQuery: AppliedAlbumsQuery | null;
  sortBy: "title" | "tracks" | "recent" | "oldest";
  sortOrder: "asc" | "desc";
  /**
   * The user picked a sort during the current search (the one on screen or the one being typed), so
   * that search is not ranked. Cleared in the same `set` that commits albums loaded with no search:
   * the next search starts on "Best match" again.
   */
  sortPickedDuringSearch: boolean;
  filterLabelTags: string[];
  availableLabels: Array<{ id: string; name: string }>;
  loadAlbums: (
    labelTag?: string | null,
    page?: number,
    pageSize?: number,
    reset?: boolean
  ) => Promise<void>;
  fetchLabels: () => Promise<void>;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: AlbumsState["sortBy"]) => void;
  setSortOrder: (order: AlbumsState["sortOrder"]) => void;
  /** `"relevance"` returns the active search to "Best match"; it is never stored as `sortBy`. */
  setSort: (
    sortBy: AlbumsState["sortBy"] | "relevance",
    sortOrder: AlbumsState["sortOrder"]
  ) => void;
  setFilterLabelTags: (tags: string[]) => void;
  toggleSelectionMode: () => void;
  toggleAlbumSelection: (id: string) => void;
  selectAllAlbums: () => void;
  clearSelection: () => void;
  setViewMode: (mode: ViewMode) => void;
  updateAlbumImage: (albumId: string, imageUrl: string | null) => void;
  _generation: number;
}

export const useAlbumsStore = create<AlbumsState>((set, get) => ({
  albums: [],
  totalCount: 0,
  hasMore: true,
  pageIndex: 0,
  isFetchingMore: false,
  lastLabelTag: null,
  isSelectionMode: false,
  selectedAlbumIds: new Set(),
  viewMode: "grid",
  isLoading: false,
  error: null,
  searchQuery: "",
  appliedQuery: null,
  sortBy: "recent",
  sortOrder: "desc",
  sortPickedDuringSearch: false,
  filterLabelTags: [],
  availableLabels: [],
  _generation: 0,

  reset: () =>
    set((state) => ({
      albums: [],
      totalCount: 0,
      hasMore: true,
      pageIndex: 0,
      isFetchingMore: false,
      lastLabelTag: null,
      isSelectionMode: false,
      selectedAlbumIds: new Set<string>(),
      isLoading: false,
      error: null,
      searchQuery: "",
      appliedQuery: null,
      sortPickedDuringSearch: false,
      filterLabelTags: [],
      availableLabels: [],
      _generation: state._generation + 1,
    })),

  loadAlbums: async (labelTag, _page, pageSize = PAGE_SIZE, reset = true) => {
    const { filterLabelTags } = get();
    let query: AppliedAlbumsQuery;

    if (reset) {
      const { searchQuery, sortBy, sortOrder, sortPickedDuringSearch, _generation } = get();
      const search = searchQuery.trim();
      query = {
        generation: _generation + 1,
        search,
        ranked: isRankedSearch(search !== "", sortPickedDuringSearch),
        sortBy,
        sortOrder,
      };
      set({ isLoading: true, error: null, pageIndex: 0, _generation: query.generation });
    } else {
      // A load-more continues the albums on screen, never the search still being typed. Before the
      // first commit, or once a newer reset is under way, there is nothing to continue.
      const { appliedQuery, isFetchingMore, _generation } = get();
      if (isFetchingMore || !appliedQuery || appliedQuery.generation !== _generation) return;
      query = appliedQuery;
      set({ isFetchingMore: true, error: null });
    }
    const gen = query.generation;

    try {
      const pageIndex = reset ? 0 : get().pageIndex;
      const from = pageIndex * pageSize;
      const to = from + pageSize - 1;

      // Published albums only (>=1 published track). /albums is a published-catalog surface,
      // and once album creation moves to upload time a draft-only album would otherwise appear
      // here. See migrations/2026-09-14_v_published_albums.sql. The ranked function returns
      // rows of the same view.
      // The exact count walks every matching album, so only a reset (the first page of a new
      // query) asks for it; a load-more continues the same query and keeps the total it counted.
      let albumRows: unknown[] | null;
      let count: number | null;

      if (query.ranked) {
        // "Best match": the database orders by how the title matches the term (exact > starts
        // with > a word starts with > contains). A function's result carries no total, so the
        // count is its own query over the same filters: a GET limited to one row, not a HEAD
        // request, which drew Supabase gateway 503s (see TracksPage fetchCounts).
        const [rankedResult, countResult] = await Promise.all([
          supabase
            .rpc("search_published_albums", {
              p_search: query.search,
              p_label_tags: labelTag
                ? [labelTag]
                : filterLabelTags.length > 0
                  ? filterLabelTags
                  : undefined,
              p_limit: pageSize,
              p_offset: from,
            })
            .select(ALBUM_SELECT),
          reset
            ? filterAlbums(
                supabase
                  .from("v_published_albums")
                  .select("id", { count: "exact" })
                  .limit(1),
                labelTag,
                filterLabelTags,
                query.search
              )
            : null,
        ]);
        if (rankedResult.error) throw rankedResult.error;
        if (countResult?.error) throw countResult.error;
        albumRows = rankedResult.data;
        count = countResult?.count ?? null;
      } else {
        // Map sortBy to database column for server-side ordering
        let orderColumn: string;
        let ascending: boolean;
        switch (query.sortBy) {
          case "title":
            orderColumn = "title";
            ascending = query.sortOrder === "asc";
            break;
          case "tracks":
            // Sort by the number the card shows. total_tracks counts every album_tracks link,
            // drafts included; published_track_count counts what a viewer can actually play.
            orderColumn = "published_track_count";
            ascending = query.sortOrder === "asc";
            break;
          case "oldest":
            orderColumn = "created_at";
            ascending = query.sortOrder === "asc";
            break;
          case "recent":
          default:
            orderColumn = "updated_at";
            ascending = query.sortOrder === "asc";
            break;
        }

        const view = supabase.from("v_published_albums");
        const sortedResult = await filterAlbums(
          (reset ? view.select(ALBUM_SELECT, { count: "exact" }) : view.select(ALBUM_SELECT))
            .order(orderColumn, { ascending })
            .range(from, to),
          labelTag,
          filterLabelTags,
          query.search
        );
        if (sortedResult.error) throw sortedResult.error;
        albumRows = sortedResult.data;
        count = sortedResult.count;
      }

      const newAlbums: AlbumView[] = (albumRows || []).map((a: any) => {
        const artistName = a.artists?.name || "";
        const totalTracks = a.published_track_count ?? 0;
        const coverUrl = a.cover_art_url || a.artwork_url || "";

        return {
          id: a.id as string,
          title: a.title,
          artist: artistName,
          coverUrl,
          releaseDate: a.release_date || "",
          tracks: [],
          genre: a.genre || "Unknown",
          description: a.description || undefined,
          isPublic: a.is_published ?? true,
          type: getAlbumType(totalTracks),
          label: a.label_tag || undefined,
          totalTracks,
          totalLength: a.total_length || undefined,
          createdAt: a.created_at || "",
          updatedAt: a.updated_at || "",
          isVirtual: true as const,
          albumName: a.title,
          albumCategory: a.album_category || undefined,
        };
      });

      const hasMore = (albumRows || []).length === pageSize;

      // Discard stale results if a newer reset has started
      if (get()._generation !== gen) return;

      set((state) => {
        const combined = reset
          ? newAlbums
          : [...state.albums, ...newAlbums];
        const deduped = Array.from(
          new Map(combined.map((a) => [a.id, a])).values()
        );
        const committed = {
          albums: deduped,
          lastLabelTag: labelTag ?? null,
          isLoading: false,
          isFetchingMore: false,
          hasMore,
          pageIndex: hasMore ? pageIndex + 1 : pageIndex,
        };
        // A load-more asked for no count: the total stays the one its reset counted, but never
        // below the rows on screen (a reset that got no count fell back to its own rows).
        if (!reset) return { ...committed, totalCount: Math.max(state.totalCount, deduped.length) };
        // New rows and the query they answer land together: nothing can read one without the other.
        return {
          ...committed,
          totalCount: count ?? deduped.length,
          appliedQuery: query,
          // Albums loaded with no search end the search: the next one starts on "Best match" again.
          sortPickedDuringSearch: query.search === "" ? false : state.sortPickedDuringSearch,
        };
      });
    } catch (error: any) {
      if (get()._generation !== gen) return;
      console.error("Failed to load albums:", error);
      set({
        error: error?.message || "Failed to load albums. Please try again.",
        isLoading: false,
        isFetchingMore: false,
      });
    }
  },

  fetchLabels: async () => {
    try {
      const { data, error } = await supabase
        .from("labels")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      set({ availableLabels: data || [] });
    } catch (error) {
      console.error("Failed to fetch labels:", error);
    }
  },

  setSearchQuery: (query) => {
    set({ searchQuery: query });
    // Search is server-side — page component handles debounce + re-fetch
  },

  setSortBy: (sortBy) => {
    // Use setSort for combined updates to avoid double re-fetch
    set({ sortBy });
  },

  setSortOrder: (sortOrder) => {
    // Use setSort for combined updates to avoid double re-fetch
    set({ sortOrder });
  },

  setSort: (sortBy, sortOrder) => {
    if (sortBy === "relevance") {
      set({ sortPickedDuringSearch: false });
    } else {
      const { searchQuery, appliedQuery } = get();
      set({
        sortBy,
        sortOrder,
        // A pick during a search: the one this reload applies, or the one still on screen while a
        // cleared search's reload is pending (its rows, and the menu, still show that search).
        sortPickedDuringSearch: searchQuery.trim() !== "" || Boolean(appliedQuery?.search),
      });
    }
    const { lastLabelTag } = get();
    get().loadAlbums(lastLabelTag, undefined, PAGE_SIZE, true);
  },

  setFilterLabelTags: (tags) => {
    set({ filterLabelTags: tags });
    // A reset: the new label filter's first page, re-counted
    const { lastLabelTag } = get();
    get().loadAlbums(lastLabelTag, undefined, PAGE_SIZE, true);
  },

  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedAlbumIds: new Set(),
    })),
  toggleAlbumSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedAlbumIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedAlbumIds: next };
    }),
  selectAllAlbums: () =>
    set((state) => ({
      selectedAlbumIds: new Set(state.albums.map((a) => a.id)),
    })),
  clearSelection: () => set({ selectedAlbumIds: new Set() }),
  setViewMode: (mode) => set({ viewMode: mode }),
  updateAlbumImage: (albumId, imageUrl) => {
    set((state) => ({
      albums: state.albums.map((a) =>
        a.id === albumId ? { ...a, coverUrl: imageUrl } : a
      ),
    }));
  },
}));

// Export hook alias for legacy imports
export const useAlbums = useAlbumsStore;
